/**
 * Token-based auth state. The user types a `tt_ID.SECRET` token once
 * and we keep it in localStorage for subsequent reloads. 401 responses
 * from the API clear it automatically and bounce them back to the
 * login screen.
 */

import { useSyncExternalStore, useCallback } from 'react';

const STORAGE_KEY = 'tt-token';

type Listener = () => void;

const listeners = new Set<Listener>();

function getSnapshot(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

function notify() {
  for (const listener of listeners) listener();
}

export function setToken(value: string | null): void {
  if (value) {
    localStorage.setItem(STORAGE_KEY, value);
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
  notify();
}

export function getToken(): string | null {
  return getSnapshot();
}

export function useAuth() {
  const token = useSyncExternalStore(subscribe, getSnapshot, () => null);
  const logout = useCallback(() => setToken(null), []);
  return { token, setToken, logout };
}
